<script setup>
import PackagePrice from "@/components/settings/billing/partials/PackagePrice.vue";
import CardSpacer from "@/components/settings/ui/CardSpacer.vue";
import CardHeading from "@/components/settings/ui/CardHeading.vue";
import CardText from "@/components/settings/ui/CardText.vue";
import CardCheckmark from "@/components/settings/ui/CardCheckmark.vue";
import CardRow from "@/components/settings/ui/CardRow.vue";
import CardSection from "@/components/settings/ui/CardSection.vue";
import CardSubSection from "@/components/settings/ui/CardSubSection.vue";
import DefaultButton from "@/components/global-themed/buttons/DefaultButton.vue";
import {useBillingPackages} from "@/composables/useBillingPackages";
import {computed} from "vue";
import {useMutation, useQueryClient} from "@tanstack/vue-query";
import axios from "axios";
import Swal from "sweetalert2";

const props = defineProps({
  product: {
    type: Object,
    required: true,
    default: () => {
    },
  },
  variant: {
    type: String,
    required: false,
    default: 'primary',
  }
});

const {isYearly, billingPeriod} = useBillingPackages();


const selectedPrice = computed(() => {
  return isYearly.value ? props.product.prices.yearly : props.product.prices.monthly;
});

const isBillingPeriodMismatch = computed(() => {
  return (isYearly.value ? 'YEAR' : 'MONTH') !== billingPeriod?.value;
});


const queryClient = useQueryClient();

const {mutate: changePackage, isPending} = useMutation({
  mutationFn: async ({priceId, preview = false}) => {
    const {data} = await axios.post('/ajax/v2/billing/packages/change', {
      priceId,
      preview,
    });
    return data;
  },
  onError: (error) => {
    if (error.response?.data?.message) {
      Swal.fire({
        title: 'Cannot change plan',
        html: error.response.data.message,
        icon: 'warning',
        confirmButtonText: 'OK',
      });
    } else {
      Swal.fire({
        title: 'Cannot change plan',
        html: 'Failed to change plan. Please try again.',
        icon: 'warning',
        confirmButtonText: 'OK',
      });
    }
  },
  onSuccess: async (data, variables) => {
    if (variables.preview) {
      const previewData = data.data;
      const message = previewData.is_upgrade
        ? (previewData.amount_due > 0
          ? `Changing your plan will charge ${previewData.formatted_amount_due} to your account.`
          : `You have enough credit to cover this change. You will not be charged.`)
        : `You will receive a credit of ${previewData.formatted_credit} on your next invoice.`;

      const result = await Swal.fire({
        title: 'Confirm plan change',
        html: message,
        icon: 'question',
        showCancelButton: true,
        reverseButtons: true,
        cancelButtonText: 'No, cancel',
        confirmButtonText: 'Yes, change plan',
      });

      if (result.isConfirmed) {
        changePackage({priceId: variables.priceId, preview: false});
      }

      return;
    }
    await queryClient.invalidateQueries({queryKey: ['billing-packages']});
    await Swal.fire({
      title: `Plan changed successfully`,
      icon: 'success'
    });
  },
});
</script>

<template>
  <div class="tw-flex tw-w-80 tw-flex-col">
    <div
      v-if="product.current"
      class="tw-bg-primary tw-border-primary tw-rounded-t-2xl tw-py-3 tw-text-white tw-flex tw-justify-center tw-font-bold tw-text-base"
    >
      Current Plan
    </div>
    <div
      class="tw-w-80 tw-p-8 tw-pt-12 tw-border tw-flex tw-flex-col tw-text-neutral-900"
      :class="{
        'tw-border-primary  tw-bg-primary-100/25 tw-rounded-b-xl': product.current,
        'tw-border-neutral-300 tw-rounded-xl tw-mt-12' : !product.current
      }"
    >
      <div class="tw-flex tw-flex-col tw-gap-8 tw-items-center">
        <span class="tw-text-2xl tw-font-bold">
          {{ product.name }}
        </span>

        <PackagePrice
          :current="product.current"
          :price="selectedPrice"
          :isYearly="isYearly"
        />
      </div>

      <CardSpacer />

      <CardSection>
        <CardHeading>
          Deliverables
        </CardHeading>

        <CardSubSection>
          <CardRow>
            <CardCheckmark checked />
            <CardText>Website</CardText>
          </CardRow>

          <CardRow>
            <CardCheckmark checked />
            <CardText>Local</CardText>
          </CardRow>
        </CardSubSection>
      </CardSection>

      <CardSpacer />

      <CardSection>
        <CardHeading>
          Deliverables Quantity
        </CardHeading>

        <CardSubSection>
          <CardRow>
            <CardCheckmark checked />
            <CardText>
              {{ product.deliverables.number_of_pages }} x
              {{ product.deliverables.number_of_pages === 1 ? 'Page' : 'Pages' }}
            </CardText>
          </CardRow>
          <CardRow>
            <CardCheckmark checked />
            <CardText>
              {{ product.deliverables.number_of_locations }} x
              {{ product.deliverables.number_of_locations === 1 ? 'Location' : 'Locations' }}
            </CardText>
          </CardRow>
          <CardRow>
            <CardCheckmark checked />
            <CardText>
              {{ product.deliverables.number_of_blogs }} x
              {{ product.deliverables.number_of_blogs === 1 ? 'Blog Post' : 'Blog Posts' }}
            </CardText>
          </CardRow>
        </CardSubSection>
      </CardSection>

      <CardSpacer />

      <CardSection>
        <CardHeading>
          Agency Features
        </CardHeading>

        <CardSubSection>
          <CardRow>
            <CardCheckmark :checked="!!product.deliverables.has_whitelabel" />
            <CardText :disabled="!product.deliverables.has_whitelabel">
              White-label Features
            </CardText>
          </CardRow>
          <CardRow>
            <CardCheckmark :checked="!!product.deliverables.has_whitelabel" />
            <CardText :disabled="!product.deliverables.has_whitelabel">
              Lead-gen Audit Tool
            </CardText>
          </CardRow>
        </CardSubSection>
      </CardSection>

      <CardSpacer />

      <CardSection>
        <CardHeading>
          Agency Features
        </CardHeading>

        <CardSubSection>
          <CardRow>
            <CardCheckmark :checked="!!product.deliverables.has_whitelabel" />
            <CardText :disabled="!product.deliverables.has_whitelabel">
              White-label Features
            </CardText>
          </CardRow>
          <CardRow>
            <CardCheckmark :checked="!!product.deliverables.has_whitelabel" />
            <CardText :disabled="!product.deliverables.has_whitelabel">
              Lead-gen Audit Tool
            </CardText>
          </CardRow>
        </CardSubSection>
      </CardSection>

      <DefaultButton
        v-tooltip="{
          content: 'Please contact our support team to upgrade due to billing period mismatch.',
          placement: 'bottom',
          theme: 'dark-blue-tooltip',
          disabled: !isBillingPeriodMismatch
        }"
        :variant="variant"
        :disabled="product.current || isBillingPeriodMismatch || isPending"
        :loading="isPending"
        class="tw-mt-8"
        @click="changePackage({ priceId: selectedPrice.id, preview: true })"
      >
        Change Plan
      </DefaultButton>
    </div>
  </div>
</template>
